import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaRegCopy } from 'react-icons/fa';
import { Tag, Typography } from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import RequestIcon from 'src/screens/request/RequestIcon';
import { parseFormBuilder } from 'src/screens/request/RequestViewPage';
import UserAvatarPopover from 'src/components/UserAvatarPopover';
import RichTextEditor from 'src/components/RichTextEditor';
import {
  convertMinToHrsMin,
  convertMinToRoundedHours,
  getRequestStatusLabel,
  getStatusColor,
  getStatusColorHex,
  parseDateToMMMDD,
} from 'src/utils/stringHelpers';
import { RequestFollowersGroup } from 'src/screens/request/RequestFollowersGroup';
import useProjectAccess from 'src/hooks/useProjectAccess';

const { Text } = Typography;

export interface RequestItemProps {
  request: Record<string, any>;
  showSpentInfo?: boolean;
}

function hasOnlyImage(html: string): boolean {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const p = doc.body.querySelector('p');

  if (!p) return false;

  return (
    p.children.length === 1 &&
    p.children[0].tagName.toLowerCase() === 'img' &&
    p.textContent?.trim() === ''
  );
}

const RequestItem = ({ request, showSpentInfo }: RequestItemProps) => {
  const isCompleted = request.status === 'completed';

  const { canSeeRequestTime } = useProjectAccess();

  const screens = useBreakpoint();

  const borderColor = getStatusColorHex(request.status);

  const clientName = `by ${request.requester?.name ?? 'No user'}`;

  const formBuilder = useMemo(() => parseFormBuilder(request), [request]);

  const hasLatestCommentOnlyAnImage = request.latest_comment
    ? hasOnlyImage(request.latest_comment.text)
    : false;

    const filterFollowers = (relatedUsers: any[]) =>
      (relatedUsers ?? []).filter(
        (u: any) => u.pivot?.type === 'follower' && u.type === 'contact'
      );
  
    const [followers, setFollowers] = useState<any[]>(() =>
      filterFollowers(request.related_users)
    );
  

  if (!screens.sm) {
    return (
      <Link
        to={`/requests/${request.uuid}`}
        className="rounded-lg bg-white p-3 flex flex-col gap-4 overflow-hidden"
        style={{
          borderLeft: `3px solid ${borderColor}`,
        }}
      >
        <div className='w-full flex gap-4'>
          <div className='flex-1 flex items-center gap-2 min-w-0'>
            <div className="flex-none rounded bg-blue-50 p-2 text-2xl leading-none">
              <RequestIcon formId={formBuilder.id} />
            </div>

            <div className='flex-1 min-w-0'>
              <div className="flex-auto overflow-hidden">
                <Text className="!block !text-xs" type="secondary" ellipsis>
                  {request.title}
                </Text>

                <Text className="font-bold" ellipsis>
                  {request.subject}
                </Text>
              </div>
            </div>
          </div>

          <div className='flex gap-2 items-center'>
            <RequestFollowersGroup 
              shape='square'
              followers={followers}
              setFollowers={setFollowers}
              requestUuid={request.uuid}
            />

            <Tag color={getStatusColor(request.status)} style={{ margin: 0 }}>
              {getRequestStatusLabel(request.status)}
            </Tag>

            <Link
              to={`/requests/${request.uuid}/duplicate`}
              className="inline-block text-sm text-[#0044CC] max-sm:hidden"
            >
              <FaRegCopy />
            </Link>
          </div>
        </div>

        <div className="flex w-full items-center justify-between">
          <Text className="!text-xs" type="secondary" ellipsis>
            {`${parseDateToMMMDD(request.target_date, true)}`}
          </Text>

          {canSeeRequestTime && !showSpentInfo && (
            <div className="w-auto flex items-center">
              <Text className="!text-xs" type="secondary" ellipsis>
                {isCompleted
                  ? `${convertMinToRoundedHours(request.time_spent)} h`
                  : request.estimated_min || request.estimated_max
                  ? `${convertMinToRoundedHours(
                      request.estimated_min
                    )} - ${convertMinToRoundedHours(request.estimated_max)} h`
                  : 'No estimated time'}
              </Text>
            </div>
          )}
        </div>

        {canSeeRequestTime && showSpentInfo && (
          <div className="w-[120px] flex-auto sm:flex-none">
            <div className="text-xs text-gray-400">Time spent</div>

            <div className="text-sm">
              {convertMinToHrsMin(request.spent_in_period)} h
            </div>
          </div>
        )}

        {request.latest_comment && (
          <div className="w-full flex-none items-center">
            <Link
              to={`/requests/${request.uuid}?target_comment_id=${request.latest_comment.uuid}`}
              className="flex items-center gap-2 overflow-hidden rounded bg-grey-100 px-2 py-1"
            >
              <div className="flex-none">
                <UserAvatarPopover user={request.latest_comment.creator} />
              </div>

              {hasLatestCommentOnlyAnImage ? (
                <div className="h-full text-sm">[Image attached]</div>
              ) : (
                <RichTextEditor
                  className="comment-styles resume"
                  value={request.latest_comment.text}
                  readonly
                  noContainer
                />
              )}
            </Link>
          </div>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={`/requests/${request.uuid}`}
      className="relative box-content flex max-w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-white p-3 min-[1400px]:flex-nowrap"
      preventScrollReset
      style={{
        borderLeft: `3px solid ${borderColor}`,
      }}
    >
      <div className="flex min-w-[100px] flex-1 items-center gap-x-3 leading-none overflow-hidden">
        <div className="flex-none rounded bg-blue-50 p-2 text-2xl leading-none">
          <RequestIcon formId={formBuilder.id} />
        </div>

        <div className='flex gap-2 justify-between w-full min-w-0 flex-1'>
          <div className="flex-auto overflow-hidden min-w-0">
            <Text className="!block !text-xs" type="secondary" ellipsis>
              {request.title}
            </Text>

            <Text className="font-bold" ellipsis>
              {request.subject}
            </Text>
          </div>

          <RequestFollowersGroup
            shape='square'
            followers={followers}
            setFollowers={setFollowers}
            requestUuid={request.uuid}
            maxCount={5} 
            className="hidden sm:block min-[1400px]:hidden" 
          />
        </div>
      </div>

      <div className="flex w-full items-center gap-x-3 gap-y-2 max-[1400px]:justify-between max-md:flex-wrap min-[1400px]:w-auto min-[1400px]:justify-between">
        <RequestFollowersGroup
          shape='square' 
          followers={followers}
          setFollowers={setFollowers}
          requestUuid={request.uuid}
          className="hidden min-[1400px]:block" 
        />
        
        {request.latest_comment && (
          <div className="w-full flex-none items-center max-md:order-1 md:w-64 lg:w-80 xl:w-64 2xl:w-96">
            <Link
              to={`/requests/${request.uuid}?target_comment_id=${request.latest_comment.uuid}`}
              className="flex items-center gap-2 overflow-hidden rounded bg-grey-100 px-2 py-1"
            >
              <div className="flex-none">
                <UserAvatarPopover user={request.latest_comment.creator} />
              </div>

              {hasLatestCommentOnlyAnImage ? (
                <div className="h-full text-sm">[Image attached]</div>
              ) : (
                <RichTextEditor
                  className="comment-styles resume"
                  value={request.latest_comment.text}
                  readonly
                  noContainer
                />
              )}
            </Link>
          </div>
        )}

        <div className="w-full flex-none overflow-hidden leading-none max-sm:hidden sm:w-auto sm:flex-auto md:w-[150px] md:flex-none">
          <div className="max-sm:hidden">
            <Text className="!text-xs" type="secondary" ellipsis>
              {clientName}
            </Text>
          </div>

          <div className="text-sm max-sm:hidden">
            {isCompleted
              ? request.completed_at
                ? `Completed: ${parseDateToMMMDD(request.completed_at)}`
                : 'No completed date'
              : request.target_date
              ? `Delivery: ${parseDateToMMMDD(request.target_date, true)}`
              : 'No due date'}
          </div>

          <div className="text-sm sm:hidden">
            {`${parseDateToMMMDD(request.target_date, true)}`}
          </div>
        </div>       

        {canSeeRequestTime && !showSpentInfo && (
          <div className="w-[120px] flex-auto max-sm:hidden sm:flex-none">
            <div className="text-xs text-gray-400 max-sm:hidden">
              {isCompleted ? 'Spent' : 'Estimated'}
            </div>

            <div className="text-sm">
              {isCompleted
                ? `${convertMinToHrsMin(request.time_spent)} h`
                : request.estimated_min || request.estimated_max
                ? `${convertMinToHrsMin(
                    request.estimated_min
                  )} - ${convertMinToHrsMin(request.estimated_max)} h`
                : 'No estimated time'}
            </div>
          </div>
        )}

        <div className="flex w-full items-center justify-between sm:hidden">
          <Text className="!text-xs" type="secondary" ellipsis>
            {`${parseDateToMMMDD(request.target_date, true)}`}
          </Text>

          {canSeeRequestTime && !showSpentInfo && (
            <div className="w-auto">
              <Text className="!text-xs" type="secondary" ellipsis>
                {isCompleted
                  ? `${convertMinToRoundedHours(request.time_spent)} h`
                  : request.estimated_min || request.estimated_max
                  ? `${convertMinToRoundedHours(
                      request.estimated_min
                    )} - ${convertMinToRoundedHours(request.estimated_max)} h`
                  : 'No estimated time'}
              </Text>
            </div>
          )}
        </div>

        {canSeeRequestTime && showSpentInfo && (
          <div className="w-[120px] flex-auto sm:flex-none">
            <div className="text-xs text-gray-400">Time spent</div>

            <div className="text-sm">
              {convertMinToHrsMin(request.spent_in_period)} h
            </div>
          </div>
        )}

        <div className="right-3 top-3 flex w-[130px] flex-none items-center justify-end gap-2 max-sm:absolute">
          <RequestFollowersGroup
            shape='square'
            className='block sm:hidden' 
            maxCount={5} 
            followers={followers}
            setFollowers={setFollowers}
            requestUuid={request.uuid}
          />

          <Tag color={getStatusColor(request.status)} style={{ margin: 0 }}>
            {getRequestStatusLabel(request.status)}
          </Tag>

          <Link
            to={`/requests/${request.uuid}/duplicate`}
            className="inline-block text-sm text-[#0044CC] max-sm:hidden"
          >
            <FaRegCopy />
          </Link>
        </div>
      </div>
    </Link>
  );
};

export default RequestItem;
